import React, { useState } from 'react';
import authService from '../../services/authService';
import './ReAuthenticateModal.css';

const ReAuthenticateModal = ({ isOpen, onClose, onSuccess, reason = 'continue with this sensitive change' }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setError('Please enter your current password.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await authService.reAuthenticate(password);
      if (response && response.success && response.data) {
        const reAuthToken = response.data.reAuthToken;
        setPassword('');
        if (onSuccess) {
          onSuccess(reAuthToken);
        }
        onClose();
      } else {
        setError(response?.message || 'Authentication failed. Please try again.');
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many failed verification attempts. Please wait a few moments before trying again.');
      } else if (err.response?.status === 401) {
        setError(err.response?.data?.message || 'Invalid password. Security verification failed.');
      } else {
        setError(err.response?.data?.message || 'An unexpected error occurred during verification.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPassword('');
    setError(null);
    onClose();
  };

  return (
    <div className="reauth-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="reauth-modal-title">
      <div className="reauth-modal-card">
        <div className="reauth-modal-header">
          <div className="reauth-shield-icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
          </div>
          <div>
            <h3 id="reauth-modal-title" className="reauth-title">Security Verification Required</h3>
            <p className="reauth-subtitle">
              For your security, please enter your current password to {reason}.
            </p>
          </div>
        </div>

        {error && (
          <div className="reauth-alert reauth-alert-error" role="alert">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="reauth-form">
          <div className="reauth-field-group">
            <label htmlFor="reauth-current-password" className="reauth-label">
              Current Password
            </label>
            <div className="reauth-input-wrapper">
              <input
                id="reauth-current-password"
                type={showPassword ? 'text' : 'password'}
                className="reauth-input"
                placeholder="Enter your current password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                disabled={loading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="reauth-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                tabIndex="-1"
              >
                {showPassword ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="reauth-modal-actions">
            <button
              type="button"
              className="reauth-btn reauth-btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="reauth-btn reauth-btn-primary"
              disabled={loading || !password.trim()}
            >
              {loading ? (
                <>
                  <span className="reauth-spinner" aria-hidden="true"></span>
                  <span>Verifying...</span>
                </>
              ) : (
                'Verify'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReAuthenticateModal;
